import React, { useEffect, useRef, useState } from 'react'
import { marketingService } from '../../../../_services/marketing.service'
import CloseButton from '../../../shared/CloseButton'
import { LEGACY_PENCE_PER_CREDIT, creditsToPence, penceToCredits, poundsToPence, formatPence, formatPrice, formatUnitPence } from '../../../pricing/money'

// The message balance: prepaid pounds for marketing texts and emails, bought
// on the salon's saved card (no redirect; off-session) or through hosted
// checkout. People see POUNDS only, never "credits" (pricing, Oct 2026).
// The API sends integer pence where it can (balancePence); an older response
// with only walletCredits is read at 2p each.
//
// BACKEND NOTE: both endpoints take pounds (amount, topupAmountGBP). The
// auto top-up threshold is sent in pence (thresholdPence) and, for a backend
// that still stores a credit threshold, as thresholdSms too.
// Kept for anything still importing them: 1 credit = 2p.
export const PENCE_PER_CREDIT = LEGACY_PENCE_PER_CREDIT
// Round amounts: £10/£20/£50/£100.
export const BUNDLES = [1000, 2000, 5000, 10000].map((pence) => ({ pence, gbp: pence / 100, credits: penceToCredits(pence) }))
const DEFAULT_THRESHOLD_PENCE = 400
const DEFAULT_TOPUP_PENCE = 2000
const AUTOSAVE_DELAY = 700

export const creditsForGbp = (gbp) => Math.round((gbp * 100) / PENCE_PER_CREDIT)

// Auto top-up in pence, from the new shape ({ thresholdPence, amountPence })
// or the old one ({ thresholdCredits | thresholdSms, topupAmountGBP }).
export function autoTopupPence(a) {
    const x = a || {}
    const threshold = x.thresholdPence != null ? Number(x.thresholdPence)
        : (x.thresholdCredits ?? x.thresholdSms) != null ? creditsToPence(x.thresholdCredits ?? x.thresholdSms)
            : DEFAULT_THRESHOLD_PENCE
    const amount = x.amountPence != null ? Number(x.amountPence)
        : x.topupAmountGBP ? poundsToPence(x.topupAmountGBP)
            : DEFAULT_TOPUP_PENCE
    return { enabled: !!x.enabled, thresholdPence: threshold, amountPence: amount, saved: x.amountPence != null || !!x.topupAmountGBP }
}

function unwrap(res) {
    const d = res && res.data
    if (d && d.statusCode === 1) return d.responseData || {}
    if (d && d.status === 1) return d.data || {}
    return d || {}
}

const ink = '#1A1A1E'
const muted = '#8E8E93'
const line = '#ECECF0'
const teal = '#007EA0'
const charcoal = '#29282D'

function Toggle({ on, onClick, label }) {
    return (
        <button
            type="button" role="switch" aria-checked={on} aria-label={label} onClick={onClick}
            style={{ width: 42, height: 24, borderRadius: 100, border: 'none', padding: 0, background: on ? teal : '#E2E2E7', position: 'relative', cursor: 'pointer', transition: 'background 0.18s', flexShrink: 0 }}
        >
            <span style={{ position: 'absolute', top: 2, left: on ? 20 : 2, width: 20, height: 20, borderRadius: '50%', background: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.25)', transition: 'left 0.18s' }} />
        </button>
    )
}

// No card on file (pricing, Oct 2026): the API answers 'payment_method_required'.
export const needsCardError = (e) => {
    const d = e && e.response && e.response.data
    const code = (d && ((d.error && (d.error.code || (d.error.data && d.error.data.code))) || d.code)) || (e && e.code)
    return code === 'payment_method_required'
}

export default function SmsWalletTopupModal({ onClose, onSuccess, onNeedCard, balancePence, currentBalance, prices, creditCosts, autoRecharge: initialAuto }) {
    const [selPence, setSelPence] = useState(DEFAULT_TOPUP_PENCE)
    const [busy, setBusy] = useState(false)
    const [error, setError] = useState(null)
    const [done, setDone] = useState(null)
    const [needCard, setNeedCard] = useState(false)
    const busyRef = useRef(false) // hard double-charge guard; state alone lags a fast double click

    // Pence first; a caller that only has the old credits figure still works.
    const balance = balancePence != null ? Number(balancePence) : currentBalance != null ? creditsToPence(currentBalance) : null
    const initial = autoTopupPence(initialAuto)
    const [ar, setAr] = useState({ enabled: initial.enabled, thresholdPence: initial.thresholdPence, amountPence: initial.amountPence })
    const [arStatus, setArStatus] = useState('idle') // 'idle' | 'saving' | 'saved'
    // The toggle saves itself — skip the very first render so opening the
    // panel on an already-saved setting doesn't fire a save immediately.
    const arMounted = useRef(false)
    const arSavedTimer = useRef(null)

    const effAmount = selPence / 100
    // What a text and an email cost, from the API (never hard-coded here).
    const ppc = (creditCosts && Number(creditCosts.pencePerCredit)) || PENCE_PER_CREDIT
    const textPence = prices && prices.textPence != null ? Number(prices.textPence) : ((creditCosts && creditCosts.sms) || 3) * ppc
    const emailPence = prices && prices.emailPence != null ? Number(prices.emailPence) : ((creditCosts && creditCosts.email) || 1) * ppc

    const pick = (v) => { setSelPence(v); setError(null) }

    // Hosted Stripe checkout — no saved card needed, and the salon can pay on a
    // different one. We leave the app entirely and come back via success_url, so
    // the balance is topped up by the webhook rather than by this call.
    const goToCheckout = async () => {
        if (busyRef.current) return
        busyRef.current = true
        setBusy(true); setError(null)
        try {
            const out = unwrap(await marketingService.smsWalletCheckout(effAmount))
            if (out && out.url) {
                window.location.assign(out.url)
                return  // navigating away — deliberately stay "busy"
            }
            setError('Could not open the payment page. Please try again.')
        } catch (e) {
            const msg = e && e.response && e.response.data && (e.response.data.message || (e.response.data.error && e.response.data.error.responseMessage))
            setError(msg || 'Could not open the payment page. Please try again.')
        }
        busyRef.current = false
        setBusy(false)
    }

    const performPurchase = async () => {
        if (busyRef.current) return
        busyRef.current = true
        setBusy(true); setError(null)
        try {
            const out = unwrap(await marketingService.smsWalletTopup(effAmount))
            if (out.requiresAction) {
                // The bank wants the cardholder present. Hosted checkout handles
                // that properly, so hand off rather than telling them to retry
                // something that will ask again.
                busyRef.current = false
                await goToCheckout()
                return
            } else if (out.success) {
                setDone({
                    addedPence: out.creditedPence != null ? Number(out.creditedPence) : out.credited != null ? creditsToPence(out.credited) : selPence,
                    balancePence: out.balancePence != null ? Number(out.balancePence) : out.walletCredits != null ? creditsToPence(out.walletCredits) : null,
                })
                if (onSuccess) onSuccess()
            } else {
                setError('Top-up could not be completed. Please try again.')
            }
        } catch (e) {
            const msg = e && e.response && e.response.data && (e.response.data.message || (e.response.data.error && e.response.data.error.responseMessage))
            // No card on file: save one in the app first, where that's on offer.
            if (needsCardError(e) && onNeedCard) {
                setNeedCard(true)
                setError('Add a card first, then top up.')
                return
            }
            // No card on file isn't a failure, it's just a different route: send
            // them to checkout instead of dead-ending on an error they can't fix
            // from here.
            if (/no saved card|no billing account/i.test(msg || '')) {
                busyRef.current = false
                await goToCheckout()
                return
            }
            setError(msg || 'Card charge failed. Please try another card.')
        } finally {
            busyRef.current = false
            setBusy(false)
        }
    }

    // Auto top-up autosaves in the background whenever it changes — no
    // separate "Save" action, the toggle and its chips are the whole UI.
    useEffect(() => {
        if (!arMounted.current) { arMounted.current = true; return }
        setArStatus('saving')
        const t = setTimeout(async () => {
            try {
                const thresholdPence = Math.max(0, Math.round(Number(ar.thresholdPence) || 0))
                const amountPence = Math.max(0, Math.round(Number(ar.amountPence) || 0))
                await marketingService.smsWalletAutoRecharge({
                    enabled: ar.enabled,
                    thresholdPence,
                    amountPence,
                    thresholdSms: penceToCredits(thresholdPence), // a backend that still stores a credit threshold
                    topupAmountGBP: amountPence / 100,
                })
                setArStatus('saved')
                clearTimeout(arSavedTimer.current)
                arSavedTimer.current = setTimeout(() => setArStatus('idle'), 1600)
            } catch (e) { setArStatus('idle') /* non-blocking */ }
        }, AUTOSAVE_DELAY)
        return () => clearTimeout(t)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ar.enabled, ar.thresholdPence, ar.amountPence])

    useEffect(() => () => clearTimeout(arSavedTimer.current), [])

    const toggleAr = () => {
        setAr((a) => ({
            ...a,
            enabled: !a.enabled,
            // First time it's switched on with nothing saved before, default
            // the refill to whatever they are buying right now.
            amountPence: !a.enabled && !initial.saved ? selPence : a.amountPence,
        }))
    }

    const arChips = BUNDLES.some((b) => b.pence === Number(ar.amountPence))
        ? BUNDLES.map((b) => b.pence)
        // Keep an already-saved amount that is not a preset, so opening this
        // panel never quietly changes it.
        : [...BUNDLES.map((b) => b.pence), Number(ar.amountPence)].sort((a, b) => a - b)

    const tile = (active) => ({
        padding: '18px 12px 16px', borderRadius: 14, textAlign: 'center', cursor: 'pointer', fontFamily: 'inherit',
        border: `1.5px solid ${active ? teal : line}`,
        background: active ? 'rgba(0,126,160,0.05)' : '#fff',
        transition: 'border-color 0.14s, background 0.14s, transform 0.1s',
    })
    const about = (pence, unit) => (unit > 0 ? Math.floor(pence / unit) : 0)

    return (
        <div className="tw-overlay" onClick={onClose}>
            <style>{`
                @keyframes twFade { from { opacity: 0 } to { opacity: 1 } }
                @keyframes twPop { from { opacity: 0; transform: translateY(12px) scale(0.985) } to { opacity: 1; transform: none } }
                @keyframes twSlideUp { from { transform: translateY(100%) } to { transform: translateY(0) } }
                .tw-overlay { position: fixed; inset: 0; background: rgba(16,16,20,0.55); z-index: 200; display: flex; align-items: center; justify-content: center; animation: twFade 0.2s ease; backdrop-filter: blur(2px); }
                .tw-card { background: #fff; border-radius: 20px; width: min(440px, 92vw); max-height: 90vh; font-family: Inter, system-ui, sans-serif; box-shadow: 0 24px 70px rgba(0,0,0,0.28); animation: twPop 0.28s cubic-bezier(0.32,0.72,0,1); box-sizing: border-box; display: flex; flex-direction: column; overflow: hidden; }
                .tw-body { overflow-y: auto; -webkit-overflow-scrolling: touch; padding: 0 24px 24px; }
                @media (max-width: 767px) {
                    .tw-overlay { align-items: flex-end; }
                    .tw-card { width: 100%; max-width: 100%; max-height: 92vh; max-height: 92dvh; border-radius: 20px 20px 0 0; animation: twSlideUp 0.32s cubic-bezier(0.32,0.72,0,1); }
                    .tw-body { padding-bottom: max(24px, env(safe-area-inset-bottom)); }
                }
            `}</style>
            <div className="tw-card" role="dialog" aria-modal="true" aria-labelledby="tw-title" onClick={(e) => e.stopPropagation()}>

                {/* Pinned: title, balance and close never scroll away. */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '24px 24px 16px', flexShrink: 0 }}>
                    <div>
                        <h2 id="tw-title" style={{ fontSize: 18, fontWeight: 700, margin: 0, color: ink, letterSpacing: '-0.01em' }}>Top up your message balance</h2>
                        {balance != null && (
                            <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 5, marginTop: 8, background: '#F7F7F9', borderRadius: 100, padding: '4px 11px', fontSize: 12, color: muted }}>
                                Balance <strong style={{ color: balance < 0 ? '#B54708' : ink, fontSize: 12.5 }}>{formatPence(balance)}</strong>
                            </span>
                        )}
                    </div>
                    <CloseButton onClick={onClose} />
                </div>

                <div className="tw-body">
                {done ? (
                    /* ── Success ─────────────────────────────────────────── */
                    <div style={{ textAlign: 'center', padding: '8px 0 4px', animation: 'twPop 0.25s cubic-bezier(0.32,0.72,0,1)' }} aria-live="polite">
                        <div style={{ width: 52, height: 52, borderRadius: '50%', background: 'rgba(0,126,160,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px' }}>
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={teal} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12" /></svg>
                        </div>
                        <div style={{ fontSize: 17, fontWeight: 700, color: ink }}>Added {formatPence(done.addedPence)}</div>
                        {done.balancePence != null && <p style={{ fontSize: 13, color: muted, margin: '6px 0 0' }}>Your balance is now {formatPence(done.balancePence)}</p>}
                        {ar.enabled && (
                            <p style={{ fontSize: 12.5, color: teal, background: 'rgba(0,126,160,0.07)', borderRadius: 10, padding: '9px 12px', margin: '14px 0 0', lineHeight: 1.5 }}>
                                Auto top-up is on: below {formatPrice(ar.thresholdPence)}, it adds {formatPrice(ar.amountPence)}.
                            </p>
                        )}
                        <button type="button" onClick={onClose} style={{ marginTop: 18, width: '100%', height: 48, border: 'none', borderRadius: 12, background: charcoal, color: '#fff', fontSize: 14.5, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>Done</button>
                    </div>
                ) : (
                    /* ── Pick an amount ───────────────────────────────────── */
                    <>
                        <div role="radiogroup" aria-label="Amount" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 14 }}>
                            {BUNDLES.map((b) => {
                                const active = selPence === b.pence
                                return (
                                    <button type="button" role="radio" aria-checked={active} key={b.pence} style={tile(active)} onClick={() => pick(b.pence)}>
                                        <span style={{ display: 'block', fontSize: 21, fontWeight: 700, color: ink, letterSpacing: '-0.01em' }}>{formatPrice(b.pence)}</span>
                                        <span style={{ display: 'block', fontSize: 11.5, color: muted, marginTop: 4 }}>about {about(b.pence, textPence).toLocaleString('en-GB')} texts</span>
                                    </button>
                                )
                            })}
                        </div>

                        <p style={{ fontSize: 13, color: muted, margin: '14px 0 0', lineHeight: 1.55 }}>
                            Texts are <strong style={{ color: ink }}>{formatUnitPence(textPence)}</strong> and emails <strong style={{ color: ink }}>{formatUnitPence(emailPence)}</strong>. {formatPrice(selPence)} sends about <strong style={{ color: ink }}>{about(selPence, emailPence).toLocaleString('en-GB')}</strong> emails or <strong style={{ color: ink }}>{about(selPence, textPence).toLocaleString('en-GB')}</strong> texts. Your balance never expires.
                        </p>

                        {/* Auto top-up — sits above the CTA since it changes what
                            the CTA is for; the toggle saves itself in the background. */}
                        <div style={{ marginTop: 20, paddingTop: 18, borderTop: `1px solid #F2F2F7` }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                                <div>
                                    <span style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                                        <p style={{ fontSize: 13.5, fontWeight: 600, color: ink, margin: 0 }}>Auto top-up when low</p>
                                        {arStatus !== 'idle' && (
                                            <span style={{ fontSize: 10.5, fontWeight: 600, color: arStatus === 'saved' ? teal : muted }} aria-live="polite">
                                                {arStatus === 'saving' ? 'Saving…' : 'Saved'}
                                            </span>
                                        )}
                                    </span>
                                    <p style={{ fontSize: 12, color: muted, margin: '2px 0 0' }}>Tops up by itself, so a campaign never stalls mid-send.</p>
                                </div>
                                <Toggle on={ar.enabled} label="Auto top-up when low" onClick={toggleAr} />
                            </div>
                            {ar.enabled && (
                                <>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 14, fontSize: 12.5, color: '#4B4B52' }}>
                                        When it drops below £
                                        <input
                                            className="tw-num" type="number" inputMode="decimal" min="0" step="1" value={Number(ar.thresholdPence) / 100}
                                            onChange={(e) => setAr({ ...ar, thresholdPence: poundsToPence(e.target.value) })}
                                            aria-label="Top up when the balance drops below, in pounds"
                                            style={{ width: 76, padding: '7px 9px', border: `1px solid ${line}`, borderRadius: 8, fontSize: 13, fontFamily: 'inherit', outline: 'none' }}
                                        />
                                        , add
                                    </label>
                                    <div role="radiogroup" aria-label="Amount to add" style={{ display: 'flex', flexWrap: 'wrap', gap: 7, marginTop: 12 }}>
                                        {arChips.map((v) => {
                                            const on = Number(ar.amountPence) === v
                                            return (
                                                <button
                                                    type="button" role="radio" aria-checked={on}
                                                    key={v}
                                                    onClick={() => setAr({ ...ar, amountPence: v })}
                                                    style={{ padding: '7px 13px', borderRadius: 100, border: `1px solid ${on ? teal : line}`, background: on ? teal : '#fff', fontSize: 12.5, fontWeight: on ? 700 : 500, color: on ? '#fff' : ink, cursor: 'pointer', fontFamily: 'inherit', transition: 'background 0.12s, color 0.12s, border-color 0.12s' }}
                                                >
                                                    {formatPrice(v)}
                                                </button>
                                            )
                                        })}
                                    </div>
                                </>
                            )}
                        </div>

                        {/* Summary — what the Buy button is actually about to do */}
                        <div style={{ marginTop: 18, background: '#F7F7F9', borderRadius: 12, padding: '13px 14px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                                <span style={{ fontSize: 13, color: '#4B4B52' }}>Top up</span>
                                <span style={{ fontSize: 13, fontWeight: 700, color: ink }}>{formatPence(selPence)}</span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginTop: 7, paddingTop: 7, borderTop: '1px solid #EAEAEE' }}>
                                <span style={{ fontSize: 12, color: muted }}>Auto top-up</span>
                                {ar.enabled ? (
                                    <span style={{ fontSize: 12, fontWeight: 600, color: teal, textAlign: 'right' }}>
                                        On · below {formatPrice(ar.thresholdPence)}, add {formatPrice(ar.amountPence)}
                                    </span>
                                ) : (
                                    <span style={{ fontSize: 12, fontWeight: 500, color: muted }}>Off</span>
                                )}
                            </div>
                        </div>

                        {error && (
                            <p role="alert" style={{ fontSize: 12.5, color: '#A05A1F', margin: '12px 0 0' }}>
                                {error}
                                {needCard && onNeedCard && <> <button type="button" onClick={onNeedCard} style={{ border: 0, background: 'none', padding: 0, color: teal, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', fontSize: 12.5 }}>Add a card</button></>}
                            </p>
                        )}

                        <button
                            type="button"
                            onClick={performPurchase} disabled={busy}
                            style={{ marginTop: 14, width: '100%', height: 50, border: 'none', borderRadius: 12, background: busy ? '#9BA0A6' : charcoal, color: '#fff', fontSize: 15, fontWeight: 600, cursor: busy ? 'default' : 'pointer', fontFamily: 'inherit', boxShadow: busy ? 'none' : '0 8px 20px rgba(26,26,30,0.25)', transition: 'background 0.15s' }}
                        >
                            {busy ? 'Charging…' : `Top up ${formatPrice(selPence)}`}
                        </button>
                        <p style={{ fontSize: 11.5, color: muted, margin: '8px 0 0', textAlign: 'center' }}>Charged to your card on file.</p>
                        <button
                            type="button" onClick={goToCheckout} disabled={busy}
                            style={{ marginTop: 6, width: '100%', padding: '6px 0', border: 'none', background: 'none', color: muted, fontSize: 11.5, fontWeight: 600, cursor: busy ? 'default' : 'pointer', fontFamily: 'inherit', textDecoration: 'underline' }}
                        >
                            Pay with a different card
                        </button>
                    </>
                )}
                </div>
            </div>
        </div>
    )
}
